function weekLabel(value, locale) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString(locale, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function polyline(values, width, height, padding) {
  const nums = values.map((value) => (Number.isFinite(Number(value)) ? Number(value) : null));
  const present = nums.filter((value) => value != null);
  if (!present.length) return "";
  const min = Math.min(...present);
  const max = Math.max(...present);
  const range = Math.max(max - min, 1);
  const innerW = width - padding * 2;
  const innerH = height - padding * 2;
  const step = nums.length > 1 ? innerW / (nums.length - 1) : 0;
  return nums
    .map((value, index) => {
      const yValue = value == null ? min : value;
      const x = padding + index * step;
      const y = padding + innerH - ((yValue - min) / range) * innerH;
      return `${x},${y}`;
    })
    .join(" ");
}

export function WeeklyMovementChart({ weeks = [], locale, t }) {
  if (!weeks.length) {
    return <p className="muted">{t("realEstate.casaMapa.admin.marketTrends.emptyWeekly")}</p>;
  }

  const max = Math.max(
    1,
    ...weeks.flatMap((row) => [row.priceIncreases, row.priceDecreases, row.created, row.becameUnavailable])
  );
  const groupWidth = 64;
  const height = 180;
  const padding = { top: 12, right: 12, bottom: 28, left: 8 };
  const width = Math.max(320, weeks.length * groupWidth + padding.left + padding.right);
  const innerH = height - padding.top - padding.bottom;
  const barW = 8;
  const series = [
    { key: "priceIncreases", className: "cm-trend-chart__bar--up" },
    { key: "priceDecreases", className: "cm-trend-chart__bar--down" },
    { key: "created", className: "cm-trend-chart__bar--new" },
    { key: "becameUnavailable", className: "cm-trend-chart__bar--off" },
  ];

  return (
    <div className="cm-trend-chart" role="img" aria-label={t("realEstate.casaMapa.admin.marketTrends.weeklyChartAria")}>
      <svg viewBox={`0 0 ${width} ${height}`} className="cm-trend-chart__svg">
        {weeks.map((row, index) => {
          const groupX = padding.left + index * groupWidth + 10;
          return (
            <g key={String(row.weekStart)}>
              {series.map((item, barIndex) => {
                const value = Number(row[item.key]) || 0;
                const barH = (value / max) * innerH;
                return (
                  <rect
                    key={item.key}
                    className={`cm-trend-chart__bar ${item.className}`}
                    x={groupX + barIndex * (barW + 2)}
                    y={padding.top + innerH - barH}
                    width={barW}
                    height={Math.max(barH, value ? 2 : 0)}
                    rx="2"
                  />
                );
              })}
              <text className="cm-trend-chart__tick" x={groupX + 18} y={height - 8} textAnchor="middle">
                {weekLabel(row.weekStart, locale)}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="cm-trend-chart__legend">
        <span><i className="cm-trend-chart__swatch cm-trend-chart__swatch--up" /> {t("realEstate.casaMapa.admin.marketTrends.priceIncreases")}</span>
        <span><i className="cm-trend-chart__swatch cm-trend-chart__swatch--down" /> {t("realEstate.casaMapa.admin.marketTrends.priceDecreases")}</span>
        <span><i className="cm-trend-chart__swatch cm-trend-chart__swatch--new" /> {t("realEstate.casaMapa.admin.marketTrends.newProperties")}</span>
        <span><i className="cm-trend-chart__swatch cm-trend-chart__swatch--off" /> {t("realEstate.casaMapa.admin.marketTrends.becameUnavailable")}</span>
      </div>
    </div>
  );
}

export function PriceHistoryChart({ points = [], locale, formatMoney, t }) {
  if (!points.length) {
    return <p className="muted">{t("realEstate.casaMapa.admin.marketTrends.emptyPriceHistory")}</p>;
  }

  const width = 640;
  const height = 200;
  const padding = 28;
  const prices = points.map((row) => row.medianNewPrice);
  const line = polyline(prices, width, height, padding);
  const maxCount = Math.max(1, ...points.flatMap((row) => [row.priceIncreases, row.priceDecreases]));
  const innerW = width - padding * 2;
  const innerH = height - padding * 2;
  const step = points.length > 1 ? innerW / (points.length - 1) : 0;
  const first = points[0];
  const last = points[points.length - 1];

  return (
    <div className="cm-trend-chart" role="img" aria-label={t("realEstate.casaMapa.admin.marketTrends.priceHistoryAria")}>
      <svg viewBox={`0 0 ${width} ${height}`} className="cm-trend-chart__svg">
        {points.map((row, index) => {
          const x = padding + index * step;
          const upH = ((row.priceIncreases || 0) / maxCount) * (innerH * 0.35);
          const downH = ((row.priceDecreases || 0) / maxCount) * (innerH * 0.35);
          return (
            <g key={String(row.weekStart)}>
              <rect className="cm-trend-chart__bar cm-trend-chart__bar--up" x={x - 7} y={padding + innerH - upH} width="6" height={upH || 0} rx="1" />
              <rect className="cm-trend-chart__bar cm-trend-chart__bar--down" x={x + 1} y={padding + innerH - downH} width="6" height={downH || 0} rx="1" />
            </g>
          );
        })}
        {line ? <polyline points={line} fill="none" className="cm-trend-chart__line" /> : null}
        <text className="cm-trend-chart__tick" x={padding} y={height - 6}>{weekLabel(first.weekStart, locale)}</text>
        <text className="cm-trend-chart__tick" x={width - padding} y={height - 6} textAnchor="end">{weekLabel(last.weekStart, locale)}</text>
      </svg>
      <div className="cm-trend-chart__legend">
        <span><i className="cm-trend-chart__swatch cm-trend-chart__swatch--line" /> {t("realEstate.casaMapa.admin.marketTrends.medianChangedPrice")}</span>
        <span><i className="cm-trend-chart__swatch cm-trend-chart__swatch--up" /> {t("realEstate.casaMapa.admin.marketTrends.priceIncreases")}</span>
        <span><i className="cm-trend-chart__swatch cm-trend-chart__swatch--down" /> {t("realEstate.casaMapa.admin.marketTrends.priceDecreases")}</span>
        {last?.medianNewPrice != null ? (
          <span className="muted">{t("realEstate.casaMapa.admin.marketTrends.latestMedian", { price: formatMoney(last.medianNewPrice) })}</span>
        ) : null}
      </div>
    </div>
  );
}

function asFinitePrice(value) {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function PropertyPriceSparkline({ points = [], t, formatMoney }) {
  const prices = [];
  points.forEach((row, index) => {
    const previous = asFinitePrice(row.previousPrice);
    const current = asFinitePrice(row.price);
    if (index === 0 && previous != null) prices.push(previous);
    if (current != null) prices.push(current);
  });
  if (prices.length < 2) {
    return <p className="muted">{t("realEstate.casaMapa.admin.marketTrends.emptyPropertyHistory")}</p>;
  }
  const width = 520;
  const height = 160;
  const padding = 18;
  const first = prices[0];
  const last = prices[prices.length - 1];
  return (
    <div className="cm-trend-chart">
      <svg viewBox={`0 0 ${width} ${height}`} className="cm-trend-chart__svg" aria-hidden="true">
        <polyline points={polyline(prices, width, height, padding)} fill="none" className="cm-trend-chart__line" />
        {prices.map((price, index) => {
          const nums = prices;
          const min = Math.min(...nums);
          const max = Math.max(...nums);
          const range = Math.max(max - min, 1);
          const innerW = width - padding * 2;
          const innerH = height - padding * 2;
          const step = nums.length > 1 ? innerW / (nums.length - 1) : 0;
          const x = padding + index * step;
          const y = padding + innerH - ((price - min) / range) * innerH;
          return <circle key={`${price}-${index}`} cx={x} cy={y} r="3.5" className="cm-trend-chart__dot" />;
        })}
      </svg>
      {formatMoney ? (
        <div className="cm-trend-chart__legend">
          <span>{formatMoney(first)}</span>
          <span>{formatMoney(last)}</span>
        </div>
      ) : null}
    </div>
  );
}
